import React from 'react';
import { Route, Routes, useNavigate } from 'react-router';
import Products from '../Pages/Products';
import Pricing from '../Pages/Pricing';
import Customers from '../Pages/Customers';
import Collections from '../Pages/Collections';
import ShopifyPage from '../Pages/Page';
import { PrivacyPolicy } from '../Pages/PrivacyPolicy';
import ShopifyMetaobject from '../Pages/Metaobjects';
import ShopifyDefinition from '../Pages/Definitions';
import ShopifyOrder from '../Pages/Orders';
import Translations from '../Pages/Translations';
import MenuItem from '../Pages/MenuItem';
import Dashboard from '../Pages/Dashboard';
import { TrialBanner, ChargeBanner, ChargeDowngradeBanner, UpdateScopeBanner } from '../common/Banners';
import { ToastMarkup } from '../common/ToastMarkup';

/**
 * Main application routes with banners and feature scope management
 */
export const AppRoutes = ({
  trialDays,
  planDownGrade,
  chargeAlert,
  btnLoad,
  chargeBtnLoad,
  currentPlan,
  planLoading,
  scopes,
  eventSource,
  showAppUpdate,
  toast,
  toggleToast,
  onActivateCharge,
  onDowngradePlan,
  onDismissTrial,
  onAllowScope,
  redirectToCharge,
  setChargeAlert,
  refreshPlan
}) => {
  const navigate = useNavigate();
  const normalizedPlan = (currentPlan || '').toLowerCase();
  const hasActivePaidOrBasicPlan = normalizedPlan === 'basic' || normalizedPlan === 'pro';
  const isFeatureBlocked = chargeAlert && !hasActivePaidOrBasicPlan;

  const handleActivateCharge = () => {
    const queryString = window.location.search;
    navigate('/plans' + queryString);
  };
  return (
    <Routes>
      {/* Products Route */}
      <Route 
        path="/products" 
        element={
          <>
            <TrialBanner 
              trialDays={trialDays}
              chargeBtnLoad={chargeBtnLoad}
              onActivate={handleActivateCharge}
              onDismiss={onDismissTrial}
            />
            <Products blockUpload={isFeatureBlocked} eventSource={eventSource} appUpdate={isFeatureBlocked ? false : showAppUpdate} />
            <ToastMarkup toast={toast} toggleToast={toggleToast} content={"Updated"} />
          </>
        } 
      />

      {/* Plans Route */}
      <Route 
        path='/plans' 
        element={
          <Pricing 
            chargeAlert={chargeAlert} 
            redirectToCharge={redirectToCharge} 
            chargeBtnLoad={chargeBtnLoad} 
            setChargeAlert={setChargeAlert} 
            currentPlan={currentPlan}
            planLoading={planLoading}
            refreshPlan={refreshPlan}
          />
        } 
      />

      {/* Customers Route */}
      <Route 
        path="/customers" 
        element={
          <>
            {scopes.customer.needsUpdate && (
              <s-page>
                <UpdateScopeBanner pathname="/customers" onAllow={onAllowScope} />
              </s-page>
            )}
            <Customers 
              blockUpload={isFeatureBlocked || scopes.customer.needsUpdate} 
              eventSource={eventSource} 
            />
          </>
        } 
      />

      {/* Collections Route */}
      <Route 
        path="/collections" 
        element={
          <>
            {scopes.collection.needsUpdate && (
              <s-page>
                <UpdateScopeBanner pathname="/collections" onAllow={onAllowScope} />
              </s-page>
            )}
            <Collections 
              blockUpload={isFeatureBlocked || scopes.collection.needsUpdate} 
              eventSource={eventSource} 
              appUpdate={isFeatureBlocked || scopes.collection.needsUpdate ? false : showAppUpdate} 
            />
          </>
        } 
      />

      {/* Pages Route */}
      <Route 
        path="/pages" 
        element={
          <>
            {scopes.page.needsUpdate && (
              <s-page>
                <UpdateScopeBanner pathname="/pages" onAllow={onAllowScope} />
              </s-page>
            )}
            <ShopifyPage 
              blockUpload={isFeatureBlocked || scopes.page.needsUpdate} 
              eventSource={eventSource} 
            />
          </>
        } 
      />

      {/* Metaobjects Route */}
      <Route 
        path="/metaobjects" 
        element={
          <>
            {scopes.metaobject.needsUpdate && (
              <s-page>
                <UpdateScopeBanner pathname="/metaobjects" onAllow={onAllowScope} />
              </s-page>
            )}
            <ShopifyMetaobject 
              blockUpload={isFeatureBlocked || scopes.metaobject.needsUpdate} 
              eventSource={eventSource} 
            />
          </>
        } 
      />

      {/* Definitions Route */}
      <Route 
        path="/definitions" 
        element={
          <>
            {scopes.metaobject.needsUpdate && (
              <s-page>
                <UpdateScopeBanner pathname="/definitions" onAllow={onAllowScope} />
              </s-page>
            )}
            <ShopifyDefinition blockUpload={isFeatureBlocked || scopes.metaobject.needsUpdate} eventSource={eventSource} />
          </>
        } 
      />

      {/* Orders Route */}
      {/* <Route 
        path="/orders" 
        element={
          <>
            {scopes.order.needsUpdate && <UpdateScopeBanner pathname="/orders" onAllow={onAllowScope} />}
            <ShopifyOrder 
              blockUpload={scopes.order.needsUpdate || scopes.order.chargeAlert} 
              eventSource={eventSource} 
            />
          </>
        } 
      /> */}

      {/* Translations Route */}
      <Route 
        path="/translations" 
        element={
          <>
            {scopes.translation.needsUpdate && (
              <s-page>
                <UpdateScopeBanner pathname="/translations" onAllow={onAllowScope} />
              </s-page>
            )}
            <Translations 
              blockUpload={isFeatureBlocked || scopes.translation.needsUpdate} 
              eventSource={eventSource} 
            />
          </>
        } 
      />

      {/* Menu Route */}
      <Route 
        path="/menu" 
        element={
          <>
            {scopes.menu.needsUpdate && (
              <s-page>
                <UpdateScopeBanner pathname="/menu" onAllow={onAllowScope} />
              </s-page>
            )}
            <MenuItem blockUpload={isFeatureBlocked || scopes.menu.needsUpdate} eventSource={eventSource} />
          </>
        } 
      />

      {/* Privacy Policy Route */}
      <Route path="/privacy" element={<PrivacyPolicy />} />

      {/* Default Route - Dashboard */}
      <Route 
        path="*" 
        element={
          <>
            <TrialBanner 
              trialDays={trialDays}
              chargeBtnLoad={chargeBtnLoad}
              onActivate={handleActivateCharge}
              onDismiss={onDismissTrial}
            />
            {planDownGrade && (
              <ChargeDowngradeBanner btnLoad={btnLoad} onChangeplan={onDowngradePlan} />
            )}
            {isFeatureBlocked && (
              <s-page>
                <ChargeBanner chargeBtnLoad={chargeBtnLoad} onActivate={handleActivateCharge} />
              </s-page>
            )}
            <Dashboard />
            <ToastMarkup toast={toast} toggleToast={toggleToast} content={"Updated"} />
          </>
        } 
      />
    </Routes>
  );
};
